import { Controller } from "@hotwired/stimulus";
import { Loader } from "@googlemaps/js-api-loader";

import { reducedClutterGoogleMapStyle } from "src/admin/map_styles";
import storeMarkerIconUrl from "images/map/restaurant-marker-icon.svg";

interface PickupPoint {
  name: string;
  latitude: string;
  longitude: string;
  serviceBoundary: object;
  path: string;
}

interface PickupPoints {
  pickupPoints: Array<PickupPoint>;
}

export default class extends Controller {
  static targets = ["map"];

  declare mapTarget: HTMLElement;
  declare hasMapTarget: boolean;

  map?: google.maps.Map;

  connect(): void {
    void this.connectMaps();
  }

  async connectMaps(): Promise<void> {
    const mapsKey = this.data.get("maps-key");
    if (!this.hasMapTarget || !mapsKey) return;
    await this.loadMaps(mapsKey);
    this.createMap();

    const pickupPointsPath = this.data.get("pickup-points-path");
    if (pickupPointsPath) await this.loadPickupPoints(pickupPointsPath);
  }

  disconnect(): void {
    this.map = undefined;
  }

  protected async loadMaps(mapsApiKey: string): Promise<void> {
    const loader = new Loader({ apiKey: mapsApiKey, region: "NZ", language: "en" });
    await loader.load();
  }

  protected createMap(): void {
    this.map = new google.maps.Map(this.mapTarget, {
      center: { lat: -41.2529389, lng: 174.6842181 },
      zoom: 5,
      styles: reducedClutterGoogleMapStyle,
    });
    this.map.data.setStyle({
      clickable: false,
      fillOpacity: 0.2,
      fillColor: '#0000ff',
      strokeWeight: 2,
      strokeOpacity: 0.8,
    })
  }

  protected async loadPickupPoints(path: string): Promise<void> {
    if (!this.map) return;

    const response = await fetch(path, { credentials: 'same-origin' });
    const json = (await response.json()) as PickupPoints;

    const bounds = new google.maps.LatLngBounds();

    const iconSize = 32;
    const icon = {
      url: storeMarkerIconUrl,
      scaledSize: new google.maps.Size(iconSize, iconSize),
      anchor: new google.maps.Point(iconSize/2, iconSize/2),
    };

    for (const pickupPoint of json.pickupPoints) {
      const marker = new google.maps.Marker({
        map: this.map,
        position: {
          lat: Number(pickupPoint.latitude),
          lng: Number(pickupPoint.longitude),
        },
        title: pickupPoint.name,
        icon: icon,
      });
      marker.addListener('click', () => { window.location.href = pickupPoint.path });

      const boundaryFeatures = this.map?.data?.addGeoJson(pickupPoint.serviceBoundary);

      boundaryFeatures.forEach((feature) => {
        feature.getGeometry()?.forEachLatLng((latlng) => {
          bounds.extend(latlng);
        });
      });
    }

    if (json.pickupPoints.length > 0) {
      this.map?.fitBounds(bounds, 0);
    }
  }
}
